import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useSnackbar } from "notistack";
import { useRepositories } from "../../repositories";
import { TAX_YEARS, defaultTaxYear, TaxYearConfig, TIER1_LIMIT, TIER1_RATE, TIER2_RATE } from "./taxYearConfig";
import { TaxYearSummary, ReportBasis } from "../../repositories/types";

const formatCurrency = (amount: number): string => "£" + (amount / 100).toFixed(2);
const formatGBP = (amount: number): string => "£" + amount.toFixed(2);

const TaxReport: React.FC = () => {
  const { reports } = useRepositories();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedYear, setSelectedYear] = useState<TaxYearConfig>(defaultTaxYear());
  const [basis, setBasis] = useState<ReportBasis>("cash");
  const [summary, setSummary] = useState<TaxYearSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const result = await reports.getTaxYearSummary(selectedYear, basis);
      setSummary(result);
    } catch (e) {
      enqueueSnackbar("Failed to load tax report", { variant: "error" });
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, basis]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const tier1Miles = summary ? Math.min(summary.totalMilesDriven, TIER1_LIMIT) : 0;
  const tier2Miles = summary ? Math.max(summary.totalMilesDriven - TIER1_LIMIT, 0) : 0;
  const tier1Amount = tier1Miles * TIER1_RATE;
  const tier2Amount = tier2Miles * TIER2_RATE;
  const totalIncome = summary ? summary.serviceIncome + summary.mileageBilled : 0;

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Tax Report
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={basis}
          exclusive
          onChange={(_e, value: ReportBasis | null) => {
            if (value) setBasis(value);
          }}
          aria-label="reporting basis"
        >
          <ToggleButton value="cash">Cash</ToggleButton>
          <ToggleButton value="accruals">Accruals</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip
          title={
            "Cash basis: income counted when you're paid. Accruals basis: income counted when you " +
            "perform the service, regardless of payment date. Cash basis is the HMRC default since " +
            "April 2024 — use accruals only if you've elected to use it or it's required for your business."
          }
        >
          <HelpOutlineIcon fontSize="small" />
        </Tooltip>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="tax-year-label">Tax Year</InputLabel>
          <Select
            labelId="tax-year-label"
            label="Tax Year"
            value={selectedYear.label}
            onChange={(e) => {
              const year = TAX_YEARS.find((y) => y.label === e.target.value);
              if (year) setSelectedYear(year);
            }}
          >
            {TAX_YEARS.map((y) => (
              <MenuItem key={y.label} value={y.label}>
                {y.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={() => window.print()}>
          Print
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && summary && (
        <>
          {/* Outstanding invoices callout — cash basis only; under accruals the underlying
              services are already counted in turnover regardless of payment status. */}
          {basis === "cash" && summary.outstandingInvoices.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Outstanding invoices (status: SENT) within this tax year:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {summary.outstandingInvoices.map((inv) => (
                  <li key={inv.id}>
                    {inv.invoiceName}{inv.subtotal != null ? ` — ${formatCurrency(inv.subtotal)}` : ""}
                  </li>
                ))}
              </ul>
              <Typography variant="body2" sx={{ mt: 1 }}>
                These invoices haven't been paid yet, so their income isn't counted on a cash basis until
                they're marked as paid.
              </Typography>
            </Alert>
          )}

          {/* Income section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              SA103(S) Box 9: Turnover
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Using the full SA103(F) instead? This figure goes in Box 15 (Your turnover).
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Gross income (Box 9 on SA103(S) — Turnover):
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {formatCurrency(totalIncome)}
              </Typography>
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Per-client breakdown</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Income</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.perClient.map((c) => (
                      <TableRow key={c.clientId}>
                        <TableCell>{c.clientName}</TableCell>
                        <TableCell align="right">{formatCurrency(c.serviceIncome + c.mileageBilled)}</TableCell>
                      </TableRow>
                    ))}
                    {summary.perClient.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>No data</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            {basis === "accruals" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <em>Income is shown by date of service, not date of payment.</em>
              </Typography>
            )}
          </Paper>

          {/* Mileage section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              SA103(S) Box 12: Motor Expenses (Mileage Allowance)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Using the full SA103(F) instead? This figure goes in Box 20 (Car, van and travel expenses).
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography>Total miles driven</Typography>
              <Typography>{summary.totalMilesDriven.toLocaleString()} miles</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography>First {Math.min(tier1Miles, TIER1_LIMIT).toLocaleString()} miles @ 45p</Typography>
              <Typography>{formatGBP(tier1Amount)}</Typography>
            </Box>
            {tier2Miles > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography>Remaining {tier2Miles.toLocaleString()} miles @ 25p</Typography>
                <Typography>{formatGBP(tier2Amount)}</Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                HMRC allowable mileage deduction (Box 12):
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {formatCurrency(summary.mileageAllowance)}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <em>
                Only applicable if you use HMRC simplified mileage rates. Do not use alongside actual
                vehicle costs.
              </em>
            </Typography>
          </Paper>

          {/* Footer — deadlines */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Key Deadlines — {selectedYear.label}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography>Register for Self Assessment</Typography>
              <Typography>{selectedYear.deadlines.register}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography>File online return</Typography>
              <Typography>{selectedYear.deadlines.fileOnline}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography>Pay tax owed</Typography>
              <Typography>{selectedYear.deadlines.pay}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <em>
                This report supports both cash basis and accruals basis accounting — check which applies
                to you. Remember to enter other allowable expenses (phone, equipment, insurance, etc.)
                directly on your SA103.
              </em>
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TaxReport;
